import React, { useMemo } from 'react';
import { AlertCircle, Sparkles } from 'lucide-react';
import {
  calculateSuggestedOccurrences,
  formatSeasonEndShort,
} from '../../../../utils/seasonalOccurrences';

/**
 * SeasonalSuggestion — Multi-Program Phase 3 (Sprint 310).
 *
 * Extracted verbatim from the legacy `ProgramSheet.jsx` so
 * `EnrollmentSheet` (Add Program) and `ChangeProgramDialog` can share
 * the same UX and computation surface without a parallel calculator.
 *
 * Props:
 *   programs           — full templates list (for lookup by id)
 *   selectedProgramId  — the currently-selected template
 *   startDate          — "YYYY-MM-DD" (or empty for today)
 *   intervalDays       — number
 *   limited            — bool (whether the operator has flipped to "Limited" occurrences)
 *   currentCount       — the current value of the total-occurrences input
 *                        (used to grey out the "Use N" button when already applied)
 *   onUseSuggestion    — (n: number) => void — flip Limited + set totalOccurrences
 *
 * Renders nothing when the selected template has no season metadata OR
 * when interval is missing/invalid — matching the legacy contract.
 */
const SeasonalSuggestion = ({
  programs,
  selectedProgramId,
  startDate,
  intervalDays,
  limited,
  currentCount = null,
  onUseSuggestion,
}) => {
  const program = useMemo(
    () => (programs || []).find((p) => p.id === selectedProgramId) || null,
    [programs, selectedProgramId],
  );

  const suggestion = useMemo(() => {
    if (!program) return null;
    if (!program.seasonStartMonthDay && !program.seasonEndMonthDay) return null;
    return calculateSuggestedOccurrences({
      startDate:           startDate || undefined,
      intervalDays:        parseInt(intervalDays, 10),
      seasonStartMonthDay: program.seasonStartMonthDay || null,
      seasonEndMonthDay:   program.seasonEndMonthDay   || null,
    });
  }, [program, startDate, intervalDays]);

  if (!suggestion) return null;

  const intervalNum = parseInt(intervalDays, 10) || 14;
  const endLabel = formatSeasonEndShort(program.seasonEndMonthDay || '12-31');

  if (suggestion.reason === 'past_season') {
    return (
      <div
        className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
        data-testid="seasonal-suggestion-past"
      >
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Start date is past the season end.</div>
          <div className="text-amber-700">
            The season for this program ends {endLabel}. Either pick an earlier start
            date or book the client under next season.
          </div>
        </div>
      </div>
    );
  }

  const isApplied = limited && Number(currentCount) === suggestion.count;

  return (
    <div
      className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
      data-testid="seasonal-suggestion"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-700" />
        <div className="flex-1">
          <div className="font-semibold">
            Suggested remaining visits:{' '}
            <span data-testid="seasonal-suggestion-count">{suggestion.count}</span>
          </div>
          <div className="text-emerald-700 leading-tight mt-0.5">
            Based on a {intervalNum}-day interval and a season ending{' '}
            <span className="font-medium">{endLabel}</span>
            {suggestion.reason === 'before_season' && (
              <> (counted from the season start, since the chosen start date is before it)</>
            )}
            .
          </div>
          <div className="text-[10px] text-emerald-600 mt-0.5 italic">
            Suggestion only — assignment-time, not a property of the program. Override below.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onUseSuggestion?.(suggestion.count)}
          disabled={isApplied}
          className={`text-[11px] font-semibold rounded border px-2 py-1 transition-colors flex-shrink-0 ${
            isApplied
              ? 'border-emerald-300 bg-white text-emerald-400 cursor-default'
              : 'border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100'
          }`}
          data-testid="seasonal-use-btn"
        >
          Use {suggestion.count}
        </button>
      </div>
    </div>
  );
};

export default SeasonalSuggestion;
